import { useEffect, useState } from "react";
import { MapAPI } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const statusColor = {
  new: "#737373",
  contacted: "#2563EB",
  replied: "#D97706",
  booked: "#059669",
  closed: "#7C3AED",
  unqualified: "#9CA3AF",
};

const statusBg = {
  new: "bg-neutral-100 text-neutral-700",
  contacted: "bg-blue-50 text-blue-700",
  replied: "bg-amber-50 text-amber-700",
  booked: "bg-emerald-50 text-emerald-700",
  closed: "bg-purple-50 text-purple-700",
  unqualified: "bg-neutral-100 text-neutral-500",
};

export default function Territory() {
  const [data, setData] = useState({ count: 0, prospects: [] });
  useEffect(() => { MapAPI.prospects().then(setData); }, []);

  // Center on the average of prospect coords, or fallback to Palo Alto
  let center = [37.4419, -122.1430];
  if (data.prospects.length) {
    const avgLat = data.prospects.reduce((a, p) => a + p.lat, 0) / data.prospects.length;
    const avgLon = data.prospects.reduce((a, p) => a + p.lon, 0) / data.prospects.length;
    center = [avgLat, avgLon];
  }

  const statusBuckets = data.prospects.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="territory-page">
      <PageHeader
        overline="Geography · Pipeline"
        title="Territory Heatmap"
        description="Every scraped prospect with coordinates. Pins colored by pipeline status."
        testId="territory-header"
      />
      <div className="p-10 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="lg:col-span-3 rounded-md border-neutral-200 overflow-hidden" data-testid="map-card" style={{ height: 640 }}>
          <MapContainer center={center} zoom={data.prospects.length ? 12 : 4} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {data.prospects.map(p => (
              <CircleMarker
                key={p.id}
                center={[p.lat, p.lon]}
                radius={9}
                pathOptions={{ color: "white", weight: 2, fillColor: statusColor[p.status] || "#737373", fillOpacity: 0.85 }}
              >
                <Popup>
                  <div style={{ minWidth: 180 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{p.business_name}</div>
                    <div style={{ fontSize: 11, color: "#666", marginTop: 2, fontFamily: "monospace", textTransform: "uppercase" }}>
                      {[p.industry, p.city].filter(Boolean).join(" · ")}
                    </div>
                    <div style={{ marginTop: 6, display: "inline-block", padding: "2px 8px", background: statusColor[p.status], color: "white", fontSize: 10, borderRadius: 3, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                      {p.status}
                    </div>
                    {p.phone && <div style={{ marginTop: 6, fontSize: 11 }}>{p.phone}</div>}
                    {p.website && <div style={{ marginTop: 2, fontSize: 11 }}><a href={p.website} target="_blank" rel="noreferrer">{p.website}</a></div>}
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </Card>

        <div className="space-y-4">
          <Card className="p-5 rounded-md border-neutral-200" data-testid="territory-legend">
            <div className="overline mb-3">On map</div>
            <div className="metric-num text-5xl">{data.count}</div>
            <div className="text-xs text-neutral-500 font-mono mt-2">prospects with coords</div>
          </Card>
          <Card className="p-5 rounded-md border-neutral-200">
            <div className="overline mb-4">Legend</div>
            <div className="space-y-2">
              {Object.entries(statusColor).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="status-dot" style={{ background: v }} />
                    <span className="capitalize">{k}</span>
                  </div>
                  <Badge variant="outline" className={`${statusBg[k]} text-[10px] font-mono`}>{statusBuckets[k] || 0}</Badge>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-5 rounded-md border-neutral-200 bg-neutral-50">
            <div className="overline mb-2">Tip</div>
            <div className="text-xs text-neutral-600">Only prospects added via <span className="font-mono">scrape</span> have geo coords. CSV & manual entries won't appear here.</div>
          </Card>
        </div>
      </div>
    </div>
  );
}
